import { type AbilitySummary, type Permission } from '@apm/contracts';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/http';

/** Ba cửa quyền của tab Git ở một dự án — docs/plan-git-integration.md §8.7. */
export interface GitPermissions {
  /** `false` trong lúc `/me/abilities` chưa về — khi ấy cả ba quyền đều `false`. */
  readonly known: boolean;
  /** `git:read` — thấy tab, thấy khối "Phát triển". */
  readonly read: boolean;
  /** `git:act` — gắn / ẩn liên kết tay, "Kéo ngay". */
  readonly act: boolean;
  /** `git:manage` — kết nối, kho, cấu hình. */
  readonly manage: boolean;
}

/**
 * Quyền git của người đang xem ở **một** dự án.
 *
 * Đọc đúng query `['abilities']` mà vỏ ứng dụng (`routes/_app.tsx`) đã tải, nên
 * không thêm lời gọi mạng nào. Khối "Phát triển" sống trong chi tiết task — thứ
 * mở từ Board, Gantt, Việc của tôi, hộp thư — và không nơi nào trong số đó có sẵn
 * quyền git để truyền xuống; hỏi ở đây thì mọi nơi mở task đều đúng mà không phải
 * sửa từng nơi.
 *
 * Chưa biết ⇒ **không có quyền**: người không có `git:read` không được thấy khối
 * nháy lên rồi biến mất, và không lời gọi `/git/…` nào được phát để nhận 403.
 */
export function useGitPermissions(projectId: string): GitPermissions {
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });

  const data = abilities.data;
  // Hình lạ (phản hồi không phải `AbilitySummary`) cũng là "chưa biết": hỏng về phía ẩn.
  if (!data || !Array.isArray(data.global) || !Array.isArray(data.perProject)) {
    return { known: false, read: false, act: false, manage: false };
  }

  const project = data.perProject.find((entry) => entry.projectId === projectId);
  const has = (permission: Permission): boolean =>
    data.global.includes(permission) || (project?.permissions.includes(permission) ?? false);

  return { known: true, read: has('git:read'), act: has('git:act'), manage: has('git:manage') };
}
